import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../supabase/supabase";
import { topNavStyle } from "../components/nav/topNavStyle";
import BrandWordmark from "../components/BrandWordmark";
import { PickList, PickBar, useLockClock } from "../components/PickThreeList";
import { PickThreeBackdrop } from "../components/PickThreeParts";
import PickThreeScoring from "../components/PickThreeScoring";
import { PRIMARY_BUTTON } from "../games/GameParts";
import {
  canMakeCaptain, CAPTAIN_RULE, describeWindow, dropLockedPicks, GAME_NAME, isRowLocked, loadDraft, LOCK_RULE, saveDraft,
  slateToRows, slateWindow,
} from "../games/pickThree";
import { setAuthRedirect } from "../utils/authRedirect";
import { getCached, IDLE_TTL_MS, setCached } from "../utils/queryCache";

// Pick Three for someone who has not signed up. Open to anyone, no account:
// they see this week's slate (pick_three_slate, migration 083) and choose
// three teams, held on this device as they choose. The picker is remembered as
// the place to land after signing in, from the moment the page opens, so the
// "Log in" link at the top and "Sign up to lock in your picks" both end there.
// The picker saves the draft to the account (PickThreeGame).
//
// A signed-in visitor goes straight to the real picker.
//
// This is the page an advert or a shared score points at, so it says what the
// game is, that it is free, and how it scores, before it asks for anything.

const SLATE_KEY = "pick3:slate";

function useSlate() {
  const [slate, setSlate] = useState(() => getCached(SLATE_KEY)?.value);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (slate !== undefined) return undefined;
    let cancelled = false;
    supabase.rpc("pick_three_slate").then(({ data, error }) => {
      if (cancelled) return;
      if (error) { setFailed(true); return; }
      setCached(SLATE_KEY, { value: data }, IDLE_TTL_MS);
      setSlate(data);
    });
    return () => { cancelled = true; };
  }, [slate]);
  return { slate, failed };
}

const FINE_PRINT = { fontSize: 12, lineHeight: 1.5, color: "var(--color-text-muted)", margin: "20px 0 0", textAlign: "center" };

function Shell({ children }) {
  useEffect(() => {
    const before = document.title;
    document.title = `${GAME_NAME} | LineupCFB`;
    return () => { document.title = before; };
  }, []);
  return (
    <div className="theme-auto" style={{ minHeight: "100vh", backgroundColor: "var(--color-bg-page)" }}>
      <nav style={{ ...topNavStyle({ paddingX: 16, paddingY: 12 }), display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Link to="/" aria-label="Lineup CFB home"><BrandWordmark width="clamp(100px, 25vw, 150px)" /></Link>
        <Link to="/login" style={{ fontSize: 14, fontWeight: 700, color: "var(--color-cta)", textDecoration: "none" }}>Log in</Link>
      </nav>
      <div style={{ maxWidth: 600, margin: "0 auto", padding: "16px 16px 0" }}>{children}</div>
    </div>
  );
}

function Hero({ slate, times, open = false }) {
  return (
    <PickThreeBackdrop style={{ borderRadius: 18, padding: "20px 16px", marginBottom: 16 }}>
      <div style={{ fontSize: 30, fontWeight: 900, letterSpacing: "-0.01em" }}>{GAME_NAME}</div>
      <p style={{ fontSize: 14, lineHeight: 1.5, margin: "6px 0 0", color: "rgba(255,255,255,0.9)" }}>
        Free to play. Pick the three college football teams you think score the most Lineup points this week, and make
        one your captain.
      </p>
      {times && (
        <div style={{ marginTop: 12, fontSize: 13, lineHeight: 1.55, color: "rgba(255,255,255,0.88)" }}>
          <div><b style={{ color: "#fff" }}>Week {slate.week} starts</b> {times.starts}</div>
          {open && <div><b style={{ color: "#fff" }}>Last picks</b> {times.last}</div>}
          {open && <div style={{ marginTop: 6, fontSize: 12, color: "rgba(255,255,255,0.75)" }}>{LOCK_RULE}</div>}
        </div>
      )}
    </PickThreeBackdrop>
  );
}

function PickThreePublic() {
  const { currentUser, loading } = useAuth();
  const navigate = useNavigate();
  const { slate, failed } = useSlate();

  const now = useLockClock();
  const rows = useMemo(() => slateToRows(slate), [slate]);
  const bySlug = useMemo(() => Object.fromEntries(rows.map((r) => [r.slug, r])), [rows]);
  const teams = useMemo(() => Object.fromEntries(rows.map((r) => [r.slug, { name: r.name, logo: r.logo }])), [rows]);
  const week = slate?.week ?? null;

  const [selected, setSelected] = useState([]);
  const [captain, setCaptain] = useState(null);
  // The draft is read once the week is known: it belongs to one week. A team
  // that has locked since the visitor chose it is quietly dropped, with the
  // captaincy if it held it: nothing has been saved yet, so there is nothing
  // to explain.
  useEffect(() => {
    if (week === null) return;
    const draft = loadDraft(week);
    const { kept } = dropLockedPicks(draft?.teams || [], rows, new Date());
    const keptCaptain = kept.includes(draft?.captain) ? draft.captain : null;
    setSelected(kept);
    setCaptain(keptCaptain);
    saveDraft(week, kept, keptCaptain);
  }, [week, rows]);

  // Whoever signs up or logs in from here lands on the picker.
  useEffect(() => {
    if (!loading && !currentUser) setAuthRedirect("/games/pick-three");
  }, [loading, currentUser]);

  if (loading) return null;
  if (currentUser) return <Navigate to="/games/pick-three" replace />;

  const isLocked = (slug) => isRowLocked(bySlug[slug], now);

  const toggle = (slug) => {
    if (isLocked(slug)) return;
    const next = selected.includes(slug) ? selected.filter((s) => s !== slug) : selected.length < 3 ? [...selected, slug] : selected;
    const nextCaptain = next.includes(captain) ? captain : null;
    setSelected(next);
    setCaptain(nextCaptain);
    saveDraft(week, next, nextCaptain);
  };

  const canCaptain = (slug) => canMakeCaptain(captain, slug, isLocked);
  const makeCaptain = (slug) => {
    if (!canCaptain(slug)) return;
    setCaptain(slug);
    saveDraft(week, selected, slug);
  };

  if (failed) {
    return <Shell><p style={{ fontSize: 14, color: "var(--color-text-muted)" }}>This week's games could not be loaded. Try again shortly.</p></Shell>;
  }
  if (slate === undefined) {
    return <Shell><p style={{ fontSize: 14, color: "var(--color-text-muted)" }}>Loading.</p></Shell>;
  }
  if (slate === null) {
    return (
      <Shell>
        <Hero slate={{ week: "" }} times={null} />
        <p style={{ fontSize: 14, lineHeight: 1.5, color: "var(--color-text-muted)" }}>
          Pick Three runs during the college football season. It opens again with the first week of games.
        </p>
        <button type="button" onClick={() => navigate("/signup")} style={PRIMARY_BUTTON}>Create a free account</button>
      </Shell>
    );
  }

  const weekState = slateWindow(slate, now);
  const times = describeWindow(weekState);

  if (!slate.open || weekState?.phase !== "open") {
    return (
      <Shell>
        <Hero slate={slate} times={times} />
        <p style={{ fontSize: 14, lineHeight: 1.5, color: "var(--color-text-strong)", margin: "0 0 12px" }}>
          Week {slate.week} picks have closed. The next week opens Sunday at 10am ET. Make a free account now and you can pick the moment it does.
        </p>
        <button type="button" onClick={() => navigate("/signup")} style={PRIMARY_BUTTON}>Create a free account</button>
        <p style={FINE_PRINT}>Free to play. No purchase and no prizes. Lineup CFB is not affiliated with any school, conference or the NCAA.</p>
      </Shell>
    );
  }

  const full = selected.length === 3;
  const ready = full && Boolean(captain);
  return (
    <Shell>
      <Hero slate={slate} times={times} open />
      <PickThreeScoring />
      <PickList rows={rows} selected={selected} onToggle={toggle} now={now} />
      <p style={FINE_PRINT}>Free to play. No purchase and no prizes. Lineup CFB is not affiliated with any school, conference or the NCAA.</p>
      <PickBar
        selected={selected} teams={teams} captain={captain} onCaptain={makeCaptain} canCaptain={canCaptain} onRemove={toggle}
      >
        {full && !captain && (
          <p style={{ fontSize: 12, margin: "8px 0 0", textAlign: "center", color: "var(--color-text-strong)" }}>{CAPTAIN_RULE}</p>
        )}
        <button
          type="button"
          onClick={() => navigate("/signup")}
          disabled={!ready}
          style={{ ...PRIMARY_BUTTON, marginTop: 8, opacity: ready ? 1 : 0.5, cursor: ready ? "pointer" : "default" }}
        >
          {ready ? "Sign up to lock in your picks" : full ? "Choose a captain" : `Pick ${3 - selected.length} more`}
        </button>
      </PickBar>
    </Shell>
  );
}

export default PickThreePublic;
